/**
 * The head start a seat gets to say it, as a piece of React state.
 *
 * The rule itself is in `render/seat-actions.ts` where it can be tested; this
 * is the part that needs a clock and a redraw. Same split as `use-narrow.ts`.
 *
 * Two things happen here that the pure half cannot do: the windows are advanced
 * when the counts change, and a redraw is scheduled for the moment the first one
 * runs out. Without the second, a held button would stay held until something
 * else on the table happened to cause a render - which, on a table waiting for
 * the player holding one card, could be a long time.
 *
 * Every declared action is handled in ONE hook rather than one call each: a hook
 * per entry would be a hook inside a loop, and the day a game declares two of
 * these the loop length changes and React is entitled to lose its state.
 */

import { useEffect, useRef, useState } from 'react';
import { NO_GRACE, isHeld, nextRelease, updateGrace } from '../render/seat-actions.js';
import type { BySeat, GraceMemory } from '../render/seat-actions.js';
import type { SeatActionWire } from '../net/session.js';

/** What each action watches, keyed by move: seat to hand size, here. */
export interface CountsByMove {
  [move: string]: BySeat;
}

/**
 * Returns a predicate rather than a set, because the caller asks about one seat
 * at a time and the answer depends on the clock at the moment it is asked.
 */
export function useGrace(
  actions: readonly SeatActionWire[],
  counts: CountsByMove,
): (move: string, seat: number) => boolean {
  const memory = useRef<{ [move: string]: GraceMemory }>({});
  const [, redraw] = useState(0);

  /*
   * Keyed on the counts as text, not on the object.
   *
   * The object is rebuilt on every render - it is derived from the view - so
   * depending on it would advance the windows on every redraw, which is exactly
   * the restart `updateGrace` is written to avoid.
   */
  const key = JSON.stringify(counts);
  const specs = JSON.stringify(actions.map((action) => [action.move, action.grace]));

  useEffect(() => {
    const now = Date.now();
    const next: { [move: string]: GraceMemory } = {};
    for (const action of actions) {
      if (!action.grace) continue;
      const seen = counts[action.move] ?? {};
      next[action.move] = updateGrace(
        memory.current[action.move] ?? NO_GRACE,
        seen,
        action.grace,
        now,
      );
    }
    memory.current = next;
    redraw((n) => n + 1);
    // `counts` and `actions` are covered by `key` and `specs`; see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, specs]);

  /*
   * One timer, for the first window to run out.
   *
   * The others are further away and get their own timer on the redraw this one
   * causes, so a table of four seats never holds four timers.
   */
  useEffect(() => {
    const now = Date.now();
    let soonest: number | null = null;
    for (const move of Object.keys(memory.current)) {
      const release = nextRelease(memory.current[move] as GraceMemory, now);
      if (release !== null && (soonest === null || release < soonest)) soonest = release;
    }
    if (soonest === null) return;

    const timer = setTimeout(() => redraw((n) => n + 1), Math.max(0, soonest - now) + 20);
    return () => clearTimeout(timer);
  });

  return (move: string, seat: number) => {
    const held = memory.current[move];
    return held !== undefined && isHeld(held, seat, Date.now());
  };
}
